/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { routeTable } from 'src/core/utils/routeTable';
import { getLanguageStudioBaseUrl } from 'src/core/utils/routingUtils';

const messages = defineMessages({
    modalTitle: {
        id: 'cluMigration.nextStepsModal.modalTitle',
        defaultMessage: 'Next steps'
    },
    done: {
        id: 'cluMigration.nextStepsModal.ok',
        defaultMessage: 'Done'
    },
    the: {
        id: 'cluMigration.nextStepsModal.the',
        defaultMessage: 'The'
    },
    migrationGuide: {
        id: 'cluMigration.nextStepsModal.migrationGuide',
        defaultMessage: 'migration guide'
    },
    description1a: {
        id: 'cluMigration.nextStepsModal.description1a',
        defaultMessage: "Now that you're done with copying over your applications to conversational language understanding. Follow the"
    },
    description1b: {
        id: 'cluMigration.nextStepsModal.description1b',
        defaultMessage: 'to better understand the next steps.'
    },
    description2: {
        id: 'cluMigration.nextStepsModal.description2',
        defaultMessage: 'will explain the differences between both services and what to expect in a lot more detail.'
    },
    description3: {
        id: 'cluMigration.nextStepsModal.description3',
        defaultMessage: 'If you have any concerns or feedback, please reach out to'
    },
    train: {
        id: 'cluMigration.nextStepsModal.train',
        defaultMessage: 'Train'
    },
    trainDescription: {
        id: 'cluMigration.nextStepsModal.trainDescription',
        defaultMessage: 'your projects to produce models'
    },
    deploy: {
        id: 'cluMigration.nextStepsModal.deploy',
        defaultMessage: 'Deploy'
    },
    deployDescription: {
        id: 'cluMigration.nextStepsModal.deployDescription',
        defaultMessage: 'your models to deployment slots that provide prediction endpoints'
    },
    call: {
        id: 'cluMigration.nextStepsModal.call',
        defaultMessage: 'Call'
    },
    callDescription: {
        id: 'cluMigration.nextStepsModal.callDescription',
        defaultMessage: 'deployed projects to consume the new predictions'
    },
    openCluInLanguageStudio: {
        id: 'cluMigration.nextStepsModal.openCluInLanguageStudio',
        defaultMessage: 'Open CLU in Language Studio'
    }
});

export const NextStepsModal = withModalMounter((props: BasicModalProps<object>) => {
    const { onDismiss } = props;
    const { localize } = useLocalization();

    const onRenderFooter = () => (
        <Stack horizontal tokens={{ childrenGap: 5 }} horizontalAlign="end">
            <PrimaryButton onClick={() => window.open(getLanguageStudioBaseUrl().concat(routeTable.clu.projectsPage()))}>
                {localize(messages.openCluInLanguageStudio)}
            </PrimaryButton>
            <PrimaryButton onClick={onDismiss}>{localize(messages.done)}</PrimaryButton>
        </Stack>
    );

    const renderListItem = (title: string, description: string) => (
        <LuisText>
            <LuisText fontWeight={600}>{title}</LuisText>
            &nbsp;
            <LuisText>{description}</LuisText>
        </LuisText>
    );

    const renderDescription1 = () => (
        <LuisText>
            <LuisText>{localize(messages.description1a)}</LuisText>
            &nbsp;
            <LuisText>
                <ExternalLink href="https://aka.ms/luis-migration-clu">{localize(messages.migrationGuide)}</ExternalLink>
            </LuisText>
            &nbsp;
            <LuisText>{localize(messages.description1b)}</LuisText>
        </LuisText>
    );

    const renderDescription2 = () => (
        <LuisText>
            <LuisText>{localize(messages.the)}</LuisText>
            &nbsp;
            <LuisText>
                <ExternalLink href="https://aka.ms/luis-migration-clu">{localize(messages.migrationGuide)}</ExternalLink>
            </LuisText>
            &nbsp;
            <LuisText>{localize(messages.description2)}</LuisText>
        </LuisText>
    );

    const renderDescription3 = () => (
        <LuisText>
            <LuisText>{localize(messages.description3)}</LuisText>
            &nbsp;
            <a href="mailto:luispm@microsoft.com?subject=Luis migration feedback">luispm@microsoft.com</a>
        </LuisText>
    );

    return (
        <BasicModal
            {...props}
            minWidth={560}
            onDismiss={onDismiss}
            onRenderFooter={onRenderFooter}
            modalProps={{ isBlocking: true }}
            dialogContentProps={{ title: localize(messages.modalTitle) }}>
            <Stack tokens={{ childrenGap: 20 }}>
                {renderDescription1()}
                <ul>
                    <Stack tokens={{ childrenGap: 15, padding: '0 0 0 30px' }}>
                        <li>{renderListItem(localize(messages.train), localize(messages.trainDescription))}</li>
                        <li>{renderListItem(localize(messages.deploy), localize(messages.deployDescription))}</li>
                        <li>{renderListItem(localize(messages.call), localize(messages.callDescription))}</li>
                    </Stack>
                </ul>
                {renderDescription2()}
                {renderDescription3()}
            </Stack>
        </BasicModal>
    );
});
